import {AddressState, CreateOrderFormSnapshot, OrderDiscountMode} from './types'

const resolveOrderLevelDiscountPayload = (
  mode: OrderDiscountMode,
  value: number | ''
): {discount_price: number | null; discount_percent: number | null} => {
  const numericValue = value === '' ? null : Number(value)
  const hasValue =
    numericValue != null && !Number.isNaN(numericValue) && numericValue > 0

  if (!hasValue) {
    return {discount_price: null, discount_percent: null}
  }

  if (mode === 'percent') {
    return {discount_price: null, discount_percent: numericValue}
  }

  return {discount_price: numericValue, discount_percent: null}
}

const mapAddressToApi = (address: AddressState) => {
  const payload: Record<string, string> = {}

  if (address.addr1) payload.address_1 = address.addr1
  if (address.addr2) payload.address_2 = address.addr2
  if (address.city) payload.city = address.city
  if (address.state) payload.state = address.state
  if (address.zip) payload.zip = address.zip
  if (address.country) payload.country = address.country

  return payload
}

const toPlatformFieldDataId = (value: string | null | undefined): number | undefined => {
  if (!value?.trim()) return undefined
  const id = Number(value)
  return Number.isNaN(id) ? undefined : id
}

const formatShippingDate = (dateStr: string): string | undefined => {
  if (!dateStr?.trim()) return undefined
  return `${dateStr}T00:00:00.000Z`
}

const buildLineItemsPayload = (lineItems: CreateOrderFormSnapshot['lineItems']) =>
  lineItems
    .filter((item) => item.platform_product && item.platform_product_variant && item.quantity > 0)
    .map((item) => {
      const totalPrice = Math.max(0, item.quantity * item.price - item.discount)

      return {
        platform_product: item.platform_product,
        platform_product_variant: item.platform_product_variant,
        sku: item.sku || '',
        name: item.name || item.sku || '',
        quantity: item.quantity,
        price: item.price,
        tax_price: 0,
        discount_price: item.discount || 0,
        total_price: totalPrice,
      }
    })

/** POST/PUT /oms/orders body — custom fields are saved via a separate endpoint. */
export const buildCreateOrderPayload = (form: CreateOrderFormSnapshot) => {
  const lineItems = buildLineItemsPayload(form.lineItems)
  const paymentStatus =
    form.grandTotal > 0 && form.amountPaid >= form.grandTotal ? 'PAID' : 'UNPAID'

  const {discount_price, discount_percent} = resolveOrderLevelDiscountPayload(
    form.orderDiscountMode ?? 'amount',
    form.orderDiscountValue
  )

  const channel = toPlatformFieldDataId(form.channelSource)
  const warehouseId = toPlatformFieldDataId(form.warehouseId)
  const shippingCarrier = toPlatformFieldDataId(form.shippingMethod)
  const paymentMethod = toPlatformFieldDataId(form.paymentMethod)
  const shippingDate = formatShippingDate(form.shipmentDate)

  const customerPayload = form.customerId 
    ? { customer_id: Number(form.customerId) }
    : {
        customer: {
          first_name: form.firstName || "",
          last_name: form.lastName || "",
          display_name: form.displayName || `${form.firstName} ${form.lastName}`.trim(),
          email: form.email || "",
          phone_number: form.phone || "",
          type: "CUSTOMER"
        }
      }

  return {
    ...customerPayload,
    ...(channel != null ? {channel} : {}),
    ...(form.orderStatusApiId || form.orderStatusLabel ? {api_status: form.orderStatusApiId || form.orderStatusLabel} : {}),
    ...(warehouseId != null ? {warehouse_id: warehouseId} : {}),
    ...(shippingCarrier != null ? {shipping_carrier: shippingCarrier} : {}),
    ...(paymentMethod != null ? {payment_method: paymentMethod} : {}),
    ...(form.paymentRef ? {payment_reference: form.paymentRef} : {}),
    paid_amount: form.amountPaid,
    payment_status: paymentStatus,
    ...(shippingDate ? {shipping_date: shippingDate} : {}),
    billing_address: mapAddressToApi(form.billing),
    shipping_address: mapAddressToApi(form.shipping),
    total_price: form.grandTotal,
    discount_price,
    duty_price: form.dutyPrice ?? 0,
    shipping_price: form.shippingFee || 0,
    tax_price: form.calculatedTax || 0,
    discount_percent,
    shipment_status: 'UNSHIPPED',
    ...(form.taxPercent != null ? {tax_percent: form.taxPercent} : form.taxCode ? {tax_code: form.taxCode} : {}),
    price_list_id: form.priceListId ?? '',
    customer_notes: form.customerNotes ?? '',
    order_notes: form.orderNotes ?? '',
    order_currency: form.orderCurrency || 'USD',
    line_items: lineItems,
  }
}

export const extractCreatedOrderId = (response: any): number | string | null =>
  response?.data?.order_id ?? response?.order_id ?? response?.data?.id ?? response?.id ?? null
